// @flow

import * as React from 'react';
import { buildUrl } from './canonicalUrl';
import { RouteConsumer } from './RouterContext';
import canNavigate from './canNavigate';
import type { Route } from './types';

/** The component props for a <Link> */
export type Props = {|
  /** The browser window target */
  target?: '_blank' | '_self',

  /** A click event handler */
  onClick?: (SyntheticMouseEvent<HTMLAnchorElement>) => mixed,

  /** The contained children within the link */
  children?: React.Node,

  /** Additional classes you'd like to add */
  className: string,

  /** Class to add if the link is active */
  activeClassName: string,

  /** The link href.  Providing this is recommmended and necessary for SEO. */
  href?: ?string,

  /** The destination */
  to?: Route,

  /** The optional title */
  title?: string,

  /**
   * Optional ARIA label attribute
   */
  label?: string,

  /** Optional extra DOM data attributes
   *  All properties in this object will be added to the Link DOM element with 'data-' prefix
   */
  dataAttributes?: Object,

  /** Prevents adding rel="noreferer noopener" attributes to the link. This is useful to
   *  be able to inject history, but ensure that this flag is overridden only for internal links.
   *  Overriding this for links that point to external domains is a security vulnerability.
   */
  stripNoreferrerNoopener?: boolean,

  /** The direction of showing link */
  dir?: string,
|};

/** Function to convert data attributes */
export const convertDataAttributes = (dataAttributes: any) => {
  const ret = {};

  if (dataAttributes) {
    Object.getOwnPropertyNames(dataAttributes).forEach(attr => {
      ret[`data-${attr}`] = dataAttributes[attr];
    });
  }

  return ret;
};

/**
 * A good old link
 */
const Link = ({
  href,
  to,
  target,
  onClick,
  children,
  className,
  // eslint-disable-next-line no-unused-vars
  activeClassName,
  title,
  label,
  dataAttributes,
  stripNoreferrerNoopener,
  dir,
}: Props) => {
  const realHref = to && typeof href === 'undefined' ? buildUrl(to) : href;
  const relValue = target === '_blank' && !stripNoreferrerNoopener ? 'noopener noreferrer' : null;

  return (
    <RouteConsumer>
      {({ navigate, route }) => (
        <a
          rel={relValue}
          href={realHref}
          target={target}
          onClick={e => {
            if (onClick) {
              onClick(e);
            }
            if (e.button === 0 && !e.isDefaultPrevented()) {
              if (navigate && to && canNavigate(route, to) && navigate(to)) {
                e.preventDefault();
              }
            }
          }}
          className={className}
          title={title}
          aria-label={label}
          {...convertDataAttributes(dataAttributes)}
          {...Object.assign({}, dir ? { dir } : null)}
        >
          {children}
        </a>
      )}
    </RouteConsumer>
  );
};

Link.defaultProps = {
  target: undefined,
  onClick: undefined,
  children: null,
  className: '',
  activeClassName: '',
  to: undefined,
  href: undefined,
  title: undefined,
  dataAttributes: undefined,
  stripNoreferrerNoopener: false,
  dir: '',
};

export default Link;
